import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { submitCrmLead } from "@/lib/leads";
import { THANK_YOU } from "@/lib/thankYou";
import { Phone, Bed, Bath, Car, Maximize, Check, MapPin, Sparkles } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppFloatingButton, { WHATSAPP_NUMBER } from "@/components/WhatsAppFloatingButton";
import FaqSection from "@/components/FaqSection";
import LeadQualificationSection from "@/components/LeadQualificationSection";
import ScheduleVisitDialog from "@/components/ScheduleVisitDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";
import { usePageMeta } from "@/hooks/usePageMeta";
import { listingPath } from "@/lib/slugify";
import heroDesktop from "@/assets/lagoa-hero-desktop.webp.asset.json";
import heroMobile from "@/assets/lagoa-hero-mobile.webp.asset.json";

const PAGE_SLUG = "apartamentos-exclusivos-vista-lagoa-rodrigo-de-freitas-rj";
const PAGE_URL = `https://topclassimoveis.com/lagoa/${PAGE_SLUG}`;

interface LagoaItem {
  id: string;
  title: string;
  subtitle: string | null;
  bedrooms: number | null;
  suites: number | null;
  garage_spots: number | null;
  area_m2: number | null;
  price: number | null;
  features: string[] | null;
  image_url: string | null;
  listing_url: string | null;
  whatsapp_message: string | null;
  is_featured: boolean;
  display_order: number;
}

interface Card {
  id: string;
  title: string;
  subtitle?: string | null;
  image?: string | null;
  price: number | null;
  area: number | null;
  bedrooms: number | null;
  suites: number | null;
  garage: number | null;
  features: string[];
  detailsUrl?: string | null;
  whatsappMessage?: string | null;
  featured: boolean;
}

const FALLBACK_CONTENT = {
  hero_title: "Apartamentos Exclusivos com Vista para a Lagoa Rodrigo de Freitas",
  hero_subtitle:
    "Selecionamos alguns dos melhores apartamentos à venda com vista para a Lagoa Rodrigo de Freitas, reunindo imóveis de alto padrão, plantas amplas, opções mobiliadas e localizações privilegiadas na Avenida Epitácio Pessoa e arredores.\n\nAgende uma visita exclusiva ou fale com um especialista da TopClass Imóveis.",
  hero_primary_label: "Ver Apartamentos Disponíveis",
  hero_secondary_label: "Falar pelo WhatsApp",
  section1_title: "Os melhores apartamentos com vista para a Lagoa em um só lugar",
  section1_body: "",
  listings_title: "Apartamentos Disponíveis",
  listings_note: "",
  match_title: "Qual apartamento combina com você?",
  match_intro:
    "Em vez de apenas listar os imóveis, ajudamos você a se identificar com o perfil que mais combina com o seu momento.",
  match_entries: [] as { area: string; price: string; description: string; featured: boolean }[],
  section3_title: "Por que morar na Lagoa Rodrigo de Freitas?",
  section3_body: "",
  section4_title: "Diferenciais dos apartamentos selecionados",
  section4_intro: "",
  section4_features: [] as string[],
  section5_title: "Não encontrou exatamente o apartamento que procura?",
  section5_body: "",
  section5_button: "Receber oportunidades exclusivas",
  section6_title: "Por que comprar com a TopClass Imóveis?",
  section6_body: "",
  section6_bullets: [] as string[],
  guide_title: "Descubra o Melhor da Lagoa Rodrigo de Freitas",
  guide_intro:
    "Mais do que um endereço privilegiado, a Lagoa Rodrigo de Freitas oferece um estilo de vida único. Cercada por natureza, gastronomia, cultura e infraestrutura completa, a região é um dos lugares mais desejados para morar no Rio de Janeiro.",
  guide_entries: [] as { emoji: string; title: string; body: string; image: string }[],
  faq: [] as { question: string; answer: string }[],
  closing_title: "Encontre o apartamento ideal com vista para a Lagoa Rodrigo de Freitas",
  closing_body: "",
  closing_button: "Falar com um Especialista",
};

const formatPrice = (p: number | null | undefined) =>
  p ? new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 }).format(p) : "Sob consulta";

const Paragraphs = ({ text, className = "" }: { text: string; className?: string }) => (
  <>{text.split(/\n{2,}/).map((p, i) => <p key={i} className={className}>{p}</p>)}</>
);

const LagoaVistaHub = () => {
  const { toast } = useToast();

  const { data: content = FALLBACK_CONTENT } = useQuery({
    queryKey: ["landing-page-content", PAGE_SLUG],
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("landing_pages").select("content").eq("slug", PAGE_SLUG).maybeSingle();
      return { ...FALLBACK_CONTENT, ...(data?.content || {}) };
    },
  });

  usePageMeta({
    title: content.hero_title,
    description:
      "Apartamentos à venda com vista para a Lagoa Rodrigo de Freitas — 2 e 3 quartos, mobiliados, alto padrão, Avenida Epitácio Pessoa. Curadoria TopClass Imóveis.",
    keywords:
      "apartamentos à venda lagoa rodrigo de freitas, apartamentos com vista para a lagoa, apartamentos de luxo lagoa, apartamentos avenida epitácio pessoa, apartamentos mobiliados lagoa, apartamentos 2 quartos lagoa, apartamentos 3 quartos lagoa, imóveis de alto padrão zona sul, imóveis de luxo rio de janeiro",
  });

  const { data: items = [] } = useQuery({
    queryKey: ["lagoa-landing-items"],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("lagoa_landing_items")
        .select("*")
        .eq("is_active", true)
        .order("is_featured", { ascending: false })
        .order("display_order", { ascending: true });
      if (error) throw error;
      return (data as LagoaItem[]) || [];
    },
  });

  const { data: dynamicListings = [] } = useQuery({
    queryKey: ["lagoa-dynamic-listings"],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("listings")
        .select("id,title,slug,price,area_m2,bedrooms,suites,garage_spots,images,features,is_featured,neighborhood")
        .eq("show_in_landing_lagoa", true)
        .eq("is_active", true)
        .order("is_featured", { ascending: false })
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const cards: Card[] = [
    ...items.map((i): Card => ({
      id: `l-${i.id}`,
      title: i.title,
      subtitle: i.subtitle,
      image: i.image_url,
      price: i.price,
      area: i.area_m2,
      bedrooms: i.bedrooms,
      suites: i.suites,
      garage: i.garage_spots,
      features: i.features || [],
      detailsUrl: i.listing_url,
      whatsappMessage: i.whatsapp_message,
      featured: i.is_featured,
    })),
    ...(dynamicListings as any[]).map((l): Card => ({
      id: `d-${l.id}`,
      title: l.title,
      subtitle: l.neighborhood || "Lagoa",
      image: l.images?.[0] || null,
      price: l.price,
      area: l.area_m2,
      bedrooms: l.bedrooms,
      suites: l.suites,
      garage: l.garage_spots,
      features: (l.features || []).slice(0, 3),
      detailsUrl: listingPath({ neighborhood: l.neighborhood || "Lagoa", slug: l.slug, id: l.id }),
      whatsappMessage: `Olá! Tenho interesse no imóvel "${l.title}" da Lagoa.`,
      featured: l.is_featured,
    })),
  ];

  useEffect(() => {
    const scripts: HTMLScriptElement[] = [];
    const add = (json: object) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.text = JSON.stringify(json);
      document.head.appendChild(s);
      scripts.push(s);
    };
    add({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: content.hero_title,
      url: PAGE_URL,
      description: content.hero_subtitle,
    });
    add({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://topclassimoveis.com/" },
        { "@type": "ListItem", position: 2, name: "Lagoa", item: "https://topclassimoveis.com/lagoa" },
        { "@type": "ListItem", position: 3, name: content.hero_title, item: PAGE_URL },
      ],
    });
    return () => scripts.forEach((s) => s.remove());
  }, [content.hero_title, content.hero_subtitle]);

  const heroWhatsApp = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    "Olá! Quero receber informações sobre os apartamentos com vista para a Lagoa."
  )}`;

  const [lead, setLead] = useState({ name: "", phone: "", range: "" });
  const [submitting, setSubmitting] = useState(false);

  const submitLead = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!lead.name || !lead.phone) return;
    setSubmitting(true);
    const { error } = await submitCrmLead({
      name: lead.name,
      phone: lead.phone,
      propertyName: `Landing Lagoa Vista - Faixa: ${lead.range || "não informada"}`,
      neighborhood: "Lagoa",
      notes: [`Lead capturado na landing ${PAGE_URL} — faixa: ${lead.range || "não informada"}.`],
    });
    setSubmitting(false);
    if (error) {
      toast({ title: "Erro ao enviar", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Cadastro recebido!", description: "Em breve nossos corretores entrarão em contato pelo WhatsApp." });
    setLead({ name: "", phone: "", range: "" });
    setTimeout(() => (window.location.href = THANK_YOU.landing), 800);
  };

  const scrollToListings = () => {
    document.getElementById("apartamentos")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="pt-16">
        {/* HERO */}
        <section className="relative bg-navy overflow-hidden">
          <picture>
            <source media="(max-width: 767px)" srcSet={heroMobile.url} />
            <img
              src={heroDesktop.url}
              alt="Vista panorâmica da Lagoa Rodrigo de Freitas, Rio de Janeiro"
              className="absolute inset-0 w-full h-full object-cover opacity-70"
              loading="eager"
              fetchPriority="high"
            />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-navy/60" />
          <div className="relative container mx-auto px-4 py-24 md:py-36 text-center">
            <h1 className="font-display text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-6 max-w-4xl mx-auto">
              {content.hero_title}
            </h1>
            <div className="text-lg md:text-xl text-white/90 font-body max-w-3xl mx-auto mb-8 space-y-4">
              <Paragraphs text={content.hero_subtitle} />
            </div>
            <div className="flex flex-wrap gap-3 justify-center">
              <Button variant="gold" size="lg" onClick={scrollToListings}>
                {content.hero_primary_label}
              </Button>
              <Button variant="whatsapp" size="lg" asChild>
                <a href={heroWhatsApp} target="_blank" rel="noopener noreferrer">
                  <Phone className="h-5 w-5" /> {content.hero_secondary_label}
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* LEAD QUALIFICATION */}
        <LeadQualificationSection source={PAGE_URL} defaultNeighborhood="Lagoa" thankYouPath={THANK_YOU.landing} />

        {/* SECTION 1 */}
        <section className="container mx-auto px-4 py-14 max-w-4xl">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-6 text-center">
            {content.section1_title}
          </h2>
          <div className="space-y-4 text-foreground/85 font-body text-lg leading-relaxed">
            <Paragraphs text={content.section1_body} />
          </div>
        </section>



        {/* SECTION 2 — LISTINGS */}
        <section id="apartamentos" className="bg-muted/40 py-14">
          <div className="container mx-auto px-4">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-3 text-center">
              {content.listings_title}
            </h2>
            {content.listings_note && (
              <p className="text-center text-muted-foreground mb-8 max-w-2xl mx-auto">{content.listings_note}</p>
            )}

            {cards.length === 0 ? (
              <p className="text-center text-muted-foreground">
                Novos apartamentos estão sendo adicionados. Fale com nossos corretores pelo WhatsApp.
              </p>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {cards.map((c) => {
                  const CardTag: any = c.detailsUrl ? "a" : "article";
                  const cardProps = c.detailsUrl
                    ? { href: c.detailsUrl, "aria-label": `Ver detalhes de ${c.title}` }
                    : {};
                  return (
                    <CardTag
                      key={c.id}
                      {...cardProps}
                      className={`group bg-card rounded-xl overflow-hidden shadow-md border border-border flex flex-col ${
                        c.detailsUrl
                          ? "cursor-pointer transition hover:shadow-xl hover:-translate-y-0.5 hover:border-gold focus:outline-none focus:ring-2 focus:ring-gold"
                          : ""
                      }`}
                    >
                      <div className="relative">
                        {c.image && (
                          <img
                            src={c.image}
                            alt={c.title}
                            className="w-full h-56 object-cover transition group-hover:scale-[1.02]"
                            loading="lazy"
                          />
                        )}
                        {c.featured && (
                          <span className="absolute top-3 left-3 bg-gold text-navy px-2 py-1 rounded font-semibold text-xs">
                            Destaque
                          </span>
                        )}
                      </div>
                      <div className="p-5 flex-1 flex flex-col">
                        <h3 className="font-display text-xl font-bold mb-1 text-foreground">{c.title}</h3>
                        {c.subtitle && <p className="text-sm text-muted-foreground mb-3">{c.subtitle}</p>}
                        {c.price && (
                          <p className="font-display text-2xl font-bold text-navy mb-3">{formatPrice(c.price)}</p>
                        )}
                        <ul className="grid grid-cols-2 gap-y-1 gap-x-3 text-sm text-foreground/80 mb-3">
                          {c.area && (
                            <li className="flex items-center gap-1.5"><Maximize className="h-3.5 w-3.5 text-gold" /> {c.area} m²</li>
                          )}
                          {c.bedrooms != null && (
                            <li className="flex items-center gap-1.5"><Bed className="h-3.5 w-3.5 text-gold" /> {c.bedrooms} quartos</li>
                          )}
                          {c.suites != null && c.suites > 0 && (
                            <li className="flex items-center gap-1.5"><Bath className="h-3.5 w-3.5 text-gold" /> {c.suites} suítes</li>
                          )}
                          {c.garage != null && c.garage > 0 && (
                            <li className="flex items-center gap-1.5"><Car className="h-3.5 w-3.5 text-gold" /> {c.garage} vagas</li>
                          )}
                        </ul>
                        {c.features.length > 0 && (
                          <ul className="text-sm space-y-1 mb-4">
                            {c.features.slice(0, 3).map((f, i) => (
                              <li key={i} className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-gold" /> {f}</li>
                            ))}
                          </ul>
                        )}
                        <div className="flex flex-wrap gap-2 mt-auto">
                          {c.detailsUrl && (
                            <Button size="sm" variant="gold" className="flex-1 pointer-events-none">
                              Ver detalhes
                            </Button>
                          )}
                          <div className="flex-1" onClick={(e) => e.stopPropagation()}>
                            <ScheduleVisitDialog propertyName={c.title} neighborhood="Lagoa">
                              <Button size="sm" variant="secondary" className="w-full text-foreground" type="button">
                                <Phone className="h-3.5 w-3.5" /> Agendar visita
                              </Button>
                            </ScheduleVisitDialog>
                          </div>
                        </div>
                      </div>
                    </CardTag>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* CTA de agendamento */}
        <section className="py-12 bg-background border-y border-border">
          <div className="container mx-auto px-4 text-center">
            <ScheduleVisitDialog
              propertyName="Apartamentos com vista para a Lagoa"
              neighborhood="Lagoa"
              label="Quero conhecer este imóvel"
              className="text-lg px-10 py-6"
            />
          </div>
        </section>


        {/* MATCH — Qual apartamento combina com você */}
        {content.match_entries && content.match_entries.length > 0 && (
          <section className="container mx-auto px-4 py-14 max-w-5xl">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4 text-center">
              {content.match_title}
            </h2>
            {content.match_intro && (
              <p className="text-center text-foreground/80 mb-10 max-w-3xl mx-auto font-body text-lg">
                {content.match_intro}
              </p>
            )}
            <div className="grid md:grid-cols-2 gap-5">
              {content.match_entries.map((m, i) => (
                <article
                  key={i}
                  className={`rounded-xl p-6 border flex flex-col ${
                    m.featured
                      ? "bg-gradient-to-br from-navy to-[#0f2444] text-white border-gold shadow-xl md:col-span-2"
                      : "bg-card border-border"
                  }`}
                >
                  <div className="flex items-baseline justify-between gap-4 mb-3 flex-wrap">
                    <h3 className={`font-display text-2xl font-bold ${m.featured ? "text-white" : "text-foreground"}`}>
                      {m.featured && <span className="mr-2">⭐</span>}
                      Apartamento {m.area}
                    </h3>
                    <span className={`font-display text-xl font-bold ${m.featured ? "text-gold" : "text-navy"}`}>
                      {m.price}
                    </span>
                  </div>
                  <p className={`font-body leading-relaxed ${m.featured ? "text-white/90" : "text-foreground/85"}`}>
                    {m.description}
                  </p>
                  <div className="mt-5">
                    <Button variant={m.featured ? "gold" : "whatsapp"} size="sm" asChild>
                      <a
                        href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
                          `Olá! Tenho interesse no apartamento de ${m.area} (${m.price}) com vista para a Lagoa.`
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Phone className="h-4 w-4" /> Quero saber mais
                      </a>
                    </Button>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}


        {/* SECTION 3 — WHY LAGOA */}
        <section className="container mx-auto px-4 py-14 max-w-4xl">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-6 text-center">
            {content.section3_title}
          </h2>
          <div className="space-y-4 text-foreground/85 font-body text-lg leading-relaxed">
            <Paragraphs text={content.section3_body} />
          </div>
        </section>

        {/* GUIA DO BAIRRO */}

        {content.guide_entries.length > 0 && (
          <section className="bg-background py-14">
            <div className="container mx-auto px-4 max-w-5xl">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4 text-center">
                {content.guide_title}
              </h2>
              {content.guide_intro && (
                <p className="text-center text-foreground/80 mb-10 max-w-3xl mx-auto font-body text-lg">
                  {content.guide_intro}
                </p>
              )}
              <div className="space-y-10">
                {content.guide_entries.map((g, i) => (
                  <article
                    key={i}
                    className={`grid md:grid-cols-2 gap-6 items-center ${
                      i % 2 === 1 ? "md:[&>div:first-child]:order-2" : ""
                    }`}
                  >
                    <div>
                      {g.image ? (
                        <img
                          src={g.image}
                          alt={g.title}
                          loading="lazy"
                          className="w-full h-64 md:h-72 object-cover rounded-xl shadow-md"
                        />
                      ) : (
                        <div className="w-full h-64 md:h-72 rounded-xl bg-muted flex items-center justify-center text-5xl">
                          {g.emoji || "🏙️"}
                        </div>
                      )}
                    </div>
                    <div>
                      <h3 className="font-display text-2xl font-bold text-foreground mb-3">
                        {g.emoji && <span className="mr-2">{g.emoji}</span>}
                        {g.title}
                      </h3>
                      <div className="text-foreground/85 font-body leading-relaxed space-y-3">
                        <Paragraphs text={g.body} />
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* SECTION 4 — DIFERENCIAIS */}
        <section className="bg-muted/40 py-14">
          <div className="container mx-auto px-4 max-w-4xl">

            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4 text-center">
              {content.section4_title}
            </h2>
            {content.section4_intro && (
              <p className="text-center text-foreground/80 mb-8 max-w-2xl mx-auto">{content.section4_intro}</p>
            )}
            <ul className="grid md:grid-cols-2 gap-3">
              {content.section4_features.map((f, i) => (
                <li key={i} className="flex items-start gap-3 bg-card rounded-lg p-4 border border-border">
                  <Sparkles className="h-5 w-5 text-gold shrink-0 mt-0.5" />
                  <span className="text-foreground/90">{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* SECTION 5 — LEAD MAGNET */}
        <section className="container mx-auto px-4 py-14 max-w-2xl">
          <div className="bg-gradient-to-br from-[#3a3a3a] to-[#2b2b2b] text-white rounded-2xl p-8 md:p-10 shadow-2xl">
            <h2 className="font-display text-2xl md:text-3xl font-bold mb-3">{content.section5_title}</h2>
            <div className="text-white/85 mb-6 font-body space-y-3">
              <Paragraphs text={content.section5_body} />
            </div>
            <form onSubmit={submitLead} className="space-y-4">
              <div>
                <Label className="text-white">Nome</Label>
                <Input required value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} className="bg-white text-foreground" />
              </div>
              <div>
                <Label className="text-white">WhatsApp</Label>
                <Input required value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} placeholder="(21) 9xxxx-xxxx" className="bg-white text-foreground" />
              </div>
              <div>
                <Label className="text-white mb-2 block">Faixa de investimento</Label>
                <RadioGroup value={lead.range} onValueChange={(v) => setLead({ ...lead, range: v })} className="space-y-2">
                  {["Até R$ 3 milhões", "Entre R$ 3 e R$ 5 milhões", "Acima de R$ 5 milhões"].map((opt) => (
                    <div key={opt} className="flex items-center gap-2">
                      <RadioGroupItem value={opt} id={opt} className="border-white text-white" />
                      <Label htmlFor={opt} className="text-white font-body cursor-pointer">{opt}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
              <Button type="submit" variant="gold" size="lg" disabled={submitting} className="w-full">
                {submitting ? "Enviando..." : content.section5_button}
              </Button>
            </form>
          </div>
        </section>

        {/* SECTION 6 — POR QUE TOPCLASS */}
        <section className="bg-muted/40 py-14">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-6 text-center">
              {content.section6_title}
            </h2>
            <div className="space-y-4 text-foreground/85 font-body text-lg leading-relaxed mb-8">
              <Paragraphs text={content.section6_body} />
            </div>
            {content.section6_bullets.length > 0 && (
              <ul className="grid md:grid-cols-2 gap-3">
                {content.section6_bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-3 bg-card rounded-lg p-4 border border-border">
                    <Check className="h-5 w-5 text-gold shrink-0 mt-0.5" />
                    <span className="text-foreground/90">{b}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        {/* FAQ */}
        {content.faq.length > 0 && (
          <FaqSection scriptId="lagoa-vista-hub" items={content.faq} />
        )}

        {/* CLOSING */}
        <section className="bg-navy py-16">
          <div className="container mx-auto px-4 text-center max-w-3xl">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">{content.closing_title}</h2>
            <div className="text-white/85 font-body mb-8 space-y-3">
              <Paragraphs text={content.closing_body} />
            </div>
            <Button variant="whatsapp" size="lg" asChild>
              <a href={heroWhatsApp} target="_blank" rel="noopener noreferrer">
                <Phone className="h-5 w-5" /> {content.closing_button}
              </a>
            </Button>
            <p className="mt-8 text-white/60 text-sm flex items-center justify-center gap-2">
              <MapPin className="h-4 w-4" /> Lagoa Rodrigo de Freitas · Rio de Janeiro
            </p>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppFloatingButton />
    </div>
  );
};

export default LagoaVistaHub;
